/**
 * The executor interface: what runs the agent's code, whatever hosts it (a browser worker now, a
 * Deno process later). Every type here crosses a MessagePort, so it is plain data: no classes, no
 * functions, bytes as Uint8Array.
 */
import type { HostCall } from "./hostCalls";

export type ReplLanguage = "js" | "python" | "bash";

/** Sync classes (spec 3.3). `not_synced` paths never leave the host. */
export type SyncClass = "work" | "data" | "uploads" | "publish" | "not_synced";

/** One file of a mounted index. Contents are inline (`text`) or fetched on first read (`source`). */
export interface MountEntry {
  /** Absolute path under the mount root, e.g. /context/INDEX.md. */
  path: string;
  /** Bytes once decoded; null when the server does not know it up front. */
  size: number | null;
  /** Hex sha256 of the decoded bytes, checked after a fetch when present. */
  sha256: string | null;
  contentType?: string | null;
  /** Inline content (small text the server sends with the index). */
  text?: string;
  /** Opaque key the host's fetcher resolves (an API link or a signed URL). */
  source?: string;
  /** The fetched bytes are gzip and need decoding (the workspace's pending overlay). */
  encoding?: "identity" | "gzip";
  mtime?: string | null;
}

export interface MountSpec {
  root: "/context" | "/workspace";
  readOnly: boolean;
  entries: MountEntry[];
}

/** One change on top of the archive, in order. */
export type SeedChange = { op: "put"; entry: MountEntry } | { op: "delete"; path: string };

/** What revives /workspace: the last archive (tar.gz, fetched whole) plus the pending changes. */
export interface WorkspaceSeed {
  archive: { source: string; version: number } | null;
  /** Files newer than the archive, applied in order (puts load lazily). */
  pending: SeedChange[];
  /** Exports, made again on first read under the user's access then (lazy). */
  exports: MountEntry[];
  /** sha256 of every file the server stores, by workspace path: what a sync need not send. */
  index?: Record<string, string>;
  /**
   * Files the last host did not send (WS2): indexed by the server, their bytes gone, so they are
   * not in the workspace. Surfacing them to the agent is WS3/SE2's.
   */
  notKept?: { path: string; size: number }[];
}

export interface RuntimeLimits {
  /** Wall clock per call (SANDBOX_CALL_TIMEOUT); a repl cell's own time, its tool calls not counted. */
  callTimeoutMs: number;
  /** A repl cell's wall clock in all, tool calls included (SANDBOX_CELL_TOTAL). */
  cellTotalMs: number;
  /**
   * How long a repl cell may wait on approvals (tools.wait), all its waits together, before a JS or
   * bash cell parks and a Python cell's wait fails (SANDBOX_CELL_PARK_SECONDS, C14).
   */
  parkAfterMs: number;
  /** Output kept for the model, from the tail. */
  outputCapBytes: number;
  /** Workspace size cap (materialized bytes). */
  workspaceMaxBytes: number;
  /**
   * An install's own clock, and a revived workspace's reinstall's before a run: unset, 120 s or
   * the call's clock if longer (a pip install also boots micropip).
   */
  installBudgetMs: number;
}

/** Where the toolchain fetches packages: the package proxy (configurable, never a public CDN). */
export interface PackageSources {
  /** Self-hosted Pyodide distribution (pyodide.mjs, pyodide.asm.wasm, lock file, wheels), ending in /. */
  pyodideIndexURL: string;
  /** PEP 503 simple index micropip installs from, ending in /. */
  pypiIndexURL: string;
  /** npm registry (metadata + tarballs), ending in /. */
  npmRegistryURL: string;
}

export interface StartOptions {
  context: MountSpec | null;
  workspace: WorkspaceSeed | null;
  limits?: Partial<RuntimeLimits>;
  env?: Record<string, string>;
  /**
   * Seeds /workspace/.steddy/installs.json (C8) when the restored workspace has none; the file wins.
   * Each listed package installs again before the first run that imports it.
   */
  installs?: InstalledPackage[];
}

export interface InstalledPackage {
  ecosystem: Ecosystem;
  name: string;
  version: string;
}

export interface ExecResult {
  stdout: string;
  stderr: string;
  exitCode: number;
  /** Output was cut to its tail. */
  truncated: boolean;
  durationMs: number;
  timedOut?: boolean;
}

export interface ReplResult extends ExecResult {
  /** The last expression's value: JSON, or a short repr. */
  value?: string;
  /** The realm was lost (timeout or crash): variables are gone, files stay. */
  resetState?: boolean;
  /**
   * The cell parked on approvals (C14, PK2): replay it from its journal once they are decided.
   * `journal` holds its clock reads and random bytes, every one it made (replayed ones too), in seq
   * order, and `journalLength` is `journal.length` (PK3: the server refuses any other count as
   * INCOMPLETE); the server keeps its tool calls.
   */
  parked?: { choices: string[]; journalLength: number; journal: ReplayEntry[] };
  /** The tools the cell called through the code-mode bridge (CM3), in seq order; only when it called one. */
  calls?: CellCall[];
  /**
   * The cell's journal (C14) as the runtime recorded it, one tool entry per answered call; with
   * `calls`. Client-local, read by tests only until a consumer exists: the server's journal (CM3)
   * is the authority on tool calls. Not what the server journaled: it includes `running` refusals
   * and pending wait answers, which CM3 does not journal.
   */
  journal?: JournalEntry[];
}

/** Options of one repl cell (C1, C14). */
export interface ReplOptions {
  /** Not read by the runtime: the host's name for the cell (PK3). */
  cellId?: string;
  /**
   * Replays these, in seq order, before running on (PK2): the cell's clock reads and random bytes
   * are answered from them until they run out. A tool call is not answered from here: it goes out
   * again under the same seq (see CONTRACTS.md C14 "Client side (CM2)" for what it gets). A read or
   * call that does not land on its seq's entry fails the cell as a replay divergence, and so does a
   * cell that ends before the last entry.
   */
  journal?: ReplayEntry[];
}

export interface CellCall {
  seq: number;
  tool: string;
  outcome: "ok" | "approval_required" | "denied" | "error";
  choice?: string;
}

/**
 * One recorded effect of a cell (C14), so a replay gives the cell what it had. The server never
 * reads the tool entries here: it keeps the authoritative journal of tool calls (CM3,
 * apps/sandbox/code_calls.py), keyed by its own hash, whose ToolEntry.value is CM3's whole
 * sandbox.tool_result answer {status, result|error|choice}, not the C11 envelope recorded here.
 * At park only the now/random entries cross (ReplayEntry, PK2).
 */
export type JournalEntry =
  | {
      seq: number;
      kind: "tool";
      name: string;
      /**
       * The runtime's: hex SHA-256 of the arguments JSON as the cell sent it. Never compared with the
       * server's key (CM3's salted HMAC over the canonical args).
       */
      argsSha256: string;
      /** Client-local: the C11 envelope the cell got, as JSON; "" when it was elided. The server's
       * own ToolEntry.value is CM3's whole sandbox.tool_result answer ({status, result|error|choice}). */
      value: string;
      /** The envelope was over CALL_ARGS_MAX_BYTES, so this local record did not keep it (the server's journal has it). */
      elided?: true;
    }
  | { seq: number; kind: "now"; value: number }
  | { seq: number; kind: "random"; /** Base64 bytes. */ value: string };

/** What a replay is handed and what crosses at park (C14): the clock reads and random bytes, never the tool entries. */
export type ReplayEntry = Exclude<JournalEntry, { kind: "tool" }>;

export interface ReadOptions {
  /** 1-based first line. */
  offset?: number;
  limit?: number;
  /** PDF pages, e.g. "3" or "2-6". */
  pages?: string;
}

export interface ReadResult {
  path: string;
  kind: "text" | "pdf" | "image" | "binary";
  text: string;
  truncated: boolean;
  totalLines?: number;
  totalPages?: number;
}

export interface WriteResult {
  path: string;
  bytes: number;
}

export interface PatchChange {
  path: string;
  op: "add" | "update" | "delete" | "move";
  movedTo?: string;
}

export interface FileInfo {
  path: string;
  name: string;
  type: "file" | "directory";
  size: number | null;
  /** Contents are in memory (false: still lazy, a read fetches it). */
  loaded: boolean;
  readOnly: boolean;
  syncClass: SyncClass | null;
  mtime: string | null;
}

export type Ecosystem = "python" | "npm";

export interface InstallResult {
  ok: boolean;
  output: string;
  installed: { name: string; version: string }[];
}

export interface DirtyPath {
  path: string;
  op: "put" | "delete";
  syncClass: Exclude<SyncClass, "not_synced">;
  size: number | null;
  mtime: string;
}

export const SANDBOX_ERROR_CODES = [
  "EROFS",
  "EACCES",
  "ENOENT",
  "EISDIR",
  "ENOTDIR",
  "EEXIST",
  "ENOTEMPTY",
  "EINVAL",
  "ENOSPC",
  "EFETCH",
  "EINTEGRITY",
  "PATCH",
  "EDIT",
  "TIMEOUT",
  "CANCELLED",
  "KILLED",
  "NOT_STARTED",
  "INTERNAL",
  // The op ran, but some or all of its changes were not saved: the hosted executor's, and the
  // browser host's when a call's changes did not reach the server by its deadline.
  "UNSAVED",
] as const;

export type SandboxErrorCode = (typeof SANDBOX_ERROR_CODES)[number];

/** A refused or failed call, as it crosses the port. */
export interface SandboxErrorData {
  code: SandboxErrorCode;
  message: string;
}

/**
 * The page's side of a cell's tool call (code mode, C11): resolves to the call's JSON envelope
 * (`ToolCallEnvelope`). The signal aborts once the cell has no use for the answer (it ended, was
 * cancelled or timed out). The runtime numbers each cell's calls (`seq`, from 1); `ticket` is the
 * host's ticket for the repl call running the cell, which the page maps to CM3's `call`.
 */
export type HostCallHandler = (call: HostCall, signal: AbortSignal) => Promise<string>;

export interface Executor {
  start(options: StartOptions): Promise<void>;
  stop(): Promise<void>;

  bash(command: string): Promise<ExecResult>;
  repl(language: ReplLanguage, code: string, options?: ReplOptions): Promise<ReplResult>;
  read(path: string, options?: ReadOptions): Promise<ReadResult>;
  write(path: string, content: string | Uint8Array): Promise<WriteResult>;
  edit(path: string, oldText: string, newText: string): Promise<WriteResult>;
  applyPatch(patch: string): Promise<PatchChange[]>;
  list(path: string): Promise<FileInfo[]>;
  stat(path: string): Promise<FileInfo>;
  install(ecosystem: Ecosystem, name: string, version?: string): Promise<InstallResult>;
  /** The install list (C8, /workspace/.steddy/installs.json): every package installed, at its version. */
  listInstalls?(): Promise<InstalledPackage[]>;

  /** Paths changed since the last drain (sync classes only); clears them. */
  drainDirty(): Promise<DirtyPath[]>;
  /** A file's bytes (fetching it if lazy). */
  exportFile(path: string): Promise<Uint8Array>;
  /** Stops the running call; the session keeps its state where it can. */
  cancel(): Promise<void>;
  /** Ends the runtime outright (state lost); a new start() revives it from the last sync. */
  kill(): Promise<void>;
}
